import { useContext, useState } from 'react';
import QBContext from '../state/context';
import { toast } from 'chameleon/ui-stack/ui/toast';
import get from 'lodash/get';
import remove from 'lodash/remove';
import buildQuizItems from '../utils/buildQuizItems';
import {
  setMyQuiz,
  saveQuiz,
  updateItemUsage,
  rollbackQuiz,
  setIsDirty,
} from '../state/actions';
import dayjs from 'dayjs';
import { updateQuiz as updateQuizGQL } from 'legacy-src/queries';
import log from 'chameleon/ui-stack/utilities/log';
import deleteAssessment from 'legacy-src/components/questionBank/QuizBuilder/utils/deleteAssessment.js';
import { tabIds } from 'legacy-src/components/questionBank/QuizBuilder/utils/constants';

export default function () {
  const [isLoading, setIsLoading] = useState(false);

  const [state = {}, dispatch] = useContext(QBContext);

  const {
    quizId,
    myQuiz,
    stimulusQuestionsInQuiz,
    questions,
    router = {},
    routes,
    params,
    client,
    activeTab,
  } = state;

  const query = get(router, 'location.query');

  const rollback = () => {
    rollbackQuiz(dispatch);
  };

  /*
   * Handles save functionality of a quiz.
   * It handles both store a new one and update an existing one.
   * */
  const save = async (quizId, quizTitle) => {
    setIsLoading(true);

    const items = buildQuizItems(state.myQuiz, state.stimulusQuestionsInQuiz);
    let ok;
    try {
      ({
        data: {
          updateQuiz: { ok },
        },
      } = await client
        .mutate({
          mutation: updateQuizGQL,
          variables: {
            itemId: quizId,
            title: quizTitle,
            content: JSON.stringify({ items }),
          },
        })
        .catch((err) => {
          log('error', err);
        }));
    } catch (err) {
      log('error', err);
    }

    if (ok) {
      saveQuiz(dispatch);
      // Item's `used_detail` should be updated only on QB/Create tab.
      const shouldUpdateItemUsage =
        routes &&
        routes.length > 0 &&
        routes.some(
          (route) => route.path && ['create', 'questions'].includes(route.path),
        );

      if (shouldUpdateItemUsage) {
        updateItemUsage(dispatch, { checkAll: true });
      }
      const savedTime = dayjs().format('H:mm:ss A');
      toast(`${quizTitle} saved at ${savedTime} `, { timeout: 2 });
    }
    setIsLoading(false);
  };

  /**
   * Handles the quiz preview.
   */
  const preview = (quizId, extraQuery = {}, useCurrentTab = false) => {
    const { subject } = params;
    const qbRoute = routes[2].path;
    let qbPage;
    if (useCurrentTab) {
      qbPage = activeTab === tabIds.BROWSE ? 'questions' : 'create';
    } else {
      qbPage = 'create';
    }

    const previewRoute = `/${subject}/${qbRoute}/${qbPage}/preview/quiz`;
    const previewRouteQuery = {
      ...query,
      ...extraQuery,
      quizId,
      autosave: true,
    };
    delete previewRouteQuery.action;
    router.push({
      pathname: previewRoute,
      query: previewRouteQuery,
    });
  };

  /**
   * Removes an item from the myQuiz array.
   * We leave it in the questions array in case the user adds it again, we won't have to hit the BE again.
   *
   * @param {object} item The object to remove from the myQuiz array
   */
  const deleteItem = async (item) => {
    const { id, item_id: itemId, stimulusId, isStimRow } = item;
    const currentItemId = itemId || id;
    const removedQuestion = remove(
      myQuiz,
      (itemId) => itemId === parseInt(currentItemId, 10),
    );
    if (!removedQuestion.length) {
      const questions = stimulusQuestionsInQuiz[stimulusId] || [];
      if (questions.length) {
        remove(
          stimulusQuestionsInQuiz[stimulusId],
          (questionId) => questionId === currentItemId,
        );
      }

      if (!stimulusQuestionsInQuiz[stimulusId].length || isStimRow) {
        delete stimulusQuestionsInQuiz[stimulusId];
        remove(myQuiz, (itemId) => itemId === stimulusId);
      }
    }

    setMyQuiz(dispatch, myQuiz, stimulusQuestionsInQuiz);
    return myQuiz;
  };

  /**
   * Removes an item from the myQuiz array and adds it to the toIndex position.
   *
   * @param {int} fromIndex, starting position of the question/ group
   * @param {int} toIndex, ending position of question/ group
   * @param {int} stimulusId, this is populated when moving an item within a stimulus group
   */
  const moveItem = (fromIndex, toIndex, stimulusId) => {
    const move = (arrayToUpdate) => {
      let element = arrayToUpdate[fromIndex];
      arrayToUpdate.splice(fromIndex, 1);
      arrayToUpdate.splice(toIndex, 0, element);
    };

    const updateStimulusArray =
      stimulusId && stimulusQuestionsInQuiz[stimulusId].length > 1;
    if (updateStimulusArray) {
      move(stimulusQuestionsInQuiz[stimulusId]);
    } else {
      move(myQuiz);
    }

    setMyQuiz(dispatch, myQuiz, stimulusQuestionsInQuiz);
  };

  const attemptDeleteQuiz = async (onDeleteCallback, onDeleteErrorCallback) => {
    const { message, canBeDeleted } = await deleteAssessment(
      client,
      quizId,
      true,
    );
    if (canBeDeleted) {
      if (onDeleteCallback) {
        onDeleteCallback(true);
      }
      setIsDirty(dispatch, false);
    } else if (onDeleteErrorCallback) {
      onDeleteErrorCallback(message);
    }
  };

  return {
    save,
    deleteItem,
    moveItem,
    preview,
    rollback,
    questions,
    isLoading,
    state,
    attemptDeleteQuiz,
  };
}
